<script setup>
import { computed, ref, watch, h, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import { useOperators } from 'dashboard/components-next/filter/operators';
import useAutomationValues from 'dashboard/composables/useAutomationValues';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { getAttributeIcon } from 'dashboard/components-next/filter/helper/filterAttributeIcons';
import { provideDropdownTeleport } from 'dashboard/components-next/dropdown-menu/base/provider';
import { AUTOMATIONS } from 'dashboard/routes/dashboard/settings/automation/constants';
import {
  generateCustomAttributeTypes,
  generateCustomAttributes,
} from 'dashboard/helper/automationHelper';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const { operators } = useOperators();
const { getConditionDropdownValues } = useAutomationValues();
const store = useStore();
const getters = useStoreGetters();

provideDropdownTeleport();

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
  store.dispatch('campaigns/get');
  store.dispatch('contacts/get');
});

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const customAttributes = computed(() => {
  const conversationAttrs = getters['attributes/getAttributesByModel'].value(
    'conversation_attribute'
  );
  const contactAttrs =
    getters['attributes/getAttributesByModel'].value('contact_attribute');
  const conversationTypes = generateCustomAttributeTypes(
    conversationAttrs || [],
    'conversation_attribute'
  );
  const contactTypes = generateCustomAttributeTypes(
    contactAttrs || [],
    'contact_attribute'
  );
  return generateCustomAttributes(
    conversationTypes,
    contactTypes,
    t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
    t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
  );
});

const allConditions = computed(() => {
  const seen = new Set();
  const merged = [];
  Object.values(AUTOMATIONS).forEach(config => {
    config.conditions.forEach(c => {
      if (!seen.has(c.key)) {
        seen.add(c.key);
        merged.push(c);
      }
    });
  });
  (customAttributes.value || []).forEach(c => merged.push(c));
  return merged;
});

const filterTypes = computed(() =>
  allConditions.value.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const isCustomAttr = Boolean(attr.customAttributeType);
    const displayName = isCustomAttr
      ? attr.name
      : t(`AUTOMATION.ATTRIBUTES.${attr.name}`);
    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      return (
        enriched || {
          value: op.value,
          label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
          hasInput: true,
          inputOverride: null,
          icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
        }
      );
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: displayName,
      label: displayName,
      icon: getAttributeIcon({
        attributeKey: attr.key,
        attributeDisplayType: attr.attributeDisplayType,
      }),
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  })
);

const apiToUi = apiConditions => {
  if (!apiConditions?.length) return [];
  return apiConditions.map(c => {
    const ft = filterTypes.value.find(f => f.attributeKey === c.attribute_key);
    const base = {
      attribute_key: c.attribute_key,
      filter_operator: c.filter_operator,
      query_operator: c.query_operator || 'and',
      custom_attribute_type: c.custom_attribute_type || '',
    };
    if (!ft) return { ...base, values: c.values?.[0] || '' };
    if (ft.inputType === 'plainText' || ft.inputType === 'date')
      return { ...base, values: c.values?.[0] || '' };
    const matching = (ft.options || []).filter(o =>
      (c.values || []).includes(o.id)
    );
    if (ft.inputType === 'searchSelect')
      return { ...base, values: matching[0] || {} };
    return { ...base, values: matching };
  });
};

const uiToApi = uiConds => {
  if (!uiConds?.length) return [];
  const enriched = uiConds.map(cond => {
    const ft = filterTypes.value.find(
      f => f.attributeKey === cond.attribute_key
    );
    return {
      ...cond,
      custom_attribute_type:
        ft && ft.attributeModel !== 'standard' ? ft.attributeModel : '',
    };
  });
  const { payload } = filterQueryGenerator(
    JSON.parse(JSON.stringify(enriched))
  );
  return payload;
};

const uiConditions = ref([]);
let skipSync = false;

watch(
  () => props.data.conditions,
  newConds => {
    if (skipSync) {
      skipSync = false;
      return;
    }
    uiConditions.value = apiToUi(newConds || []);
  },
  { immediate: true }
);

watch(
  uiConditions,
  newConds => {
    const apiConds = uiToApi(newConds);
    skipSync = true;
    emit('update', { conditions: apiConds });
  },
  { deep: true }
);

const updateField = (field, value) => {
  emit('update', { [field]: value });
};

const nodeLabel = computed({
  get() {
    return props.data.label || '';
  },
  set(value) {
    emit('update', { label: value });
  },
});

const addCondition = () => {
  uiConditions.value = [
    ...uiConditions.value,
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: [],
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ];
};

const removeCondition = index => {
  uiConditions.value = uiConditions.value.filter((_, i) => i !== index);
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1.5">
      <label class="text-[11px] font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.CONDITION.NODE_NAME') }}
      </label>
      <NextInput
        v-model="nodeLabel"
        type="text"
        size="sm"
        :placeholder="t('FLOW_BUILDER.SIDEBAR.CONDITION')"
      />
    </div>

    <div class="flex items-center gap-2">
      <select
        :value="data.condition_match"
        class="flex-1 px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
        @change="updateField('condition_match', $event.target.value)"
      >
        <option value="all">
          {{ t('FLOW_BUILDER.NODE_EDITOR.CONDITION.ALL') }}
        </option>
        <option value="any">
          {{ t('FLOW_BUILDER.NODE_EDITOR.CONDITION.ANY') }}
        </option>
      </select>
    </div>

    <div
      v-for="(condition, i) in uiConditions"
      :key="i"
      class="p-3 rounded-lg bg-n-alpha-1 border border-n-weak dark:border-n-strong"
    >
      <ConditionRow
        v-if="i === 0"
        v-model:attribute-key="uiConditions[i].attribute_key"
        v-model:filter-operator="uiConditions[i].filter_operator"
        v-model:values="uiConditions[i].values"
        :filter-types="filterTypes"
        :show-query-operator="false"
        @remove="removeCondition(i)"
      />
      <ConditionRow
        v-else
        v-model:attribute-key="uiConditions[i].attribute_key"
        v-model:filter-operator="uiConditions[i].filter_operator"
        v-model:query-operator="uiConditions[i - 1].query_operator"
        v-model:values="uiConditions[i].values"
        :filter-types="filterTypes"
        show-query-operator
        @remove="removeCondition(i)"
      />
    </div>

    <Button
      variant="ghost"
      size="sm"
      :label="t('FLOW_BUILDER.NODE_EDITOR.CONDITION.ADD')"
      icon="i-lucide-plus"
      @click="addCondition"
    />
  </div>
</template>
